import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Icon } from "../components/Icon";
import { PageTitle } from "../components/PageTitle";
import { Button, EmptyState, Field, Input, Select, Textarea } from "../components/ui";
import { useToast } from "../components/Toast";
import {
  adjustBudget,
  apiGet,
  currentSpend,
  listBudgetAdjustments,
  listRunAllowances,
  spendSummary,
  stageCapReport,
} from "../lib/api";
import type {
  BudgetAdjustment,
  JobSpendSnapshot,
  Phase2State,
  RunAllowance,
  SpendStageSummary,
  StageCapReport,
} from "../lib/types";
import { formatApiError, getStoredJobId } from "../lib/utils";

/**
 * Budget: target ledger, run allowance, stage caps and adjustment history
 * (plan section 5.10).
 *
 * The plan's "no unvalidated numbers" rule is a UI rule here as much as an API
 * one.  Three of these payloads deliberately return `null` plus a reason where
 * they cannot compute a figure honestly:
 *
 *   SpendStageSummary.cache_hit_share / cost_per_accepted_project /
 *     cost_per_useful_update  → `*_reason`, `cold_vs_warm.reason`
 *   StageCapReport.cap / used / remaining → `usage_source` ("none" when
 *     nothing was counted, which is not zero consumption)
 *
 * Every one of those is rendered as its reason.  Nothing on this page prints
 * 0 or an em dash where the API said "I could not compute this".
 */

type TargetRow = NonNullable<Phase2State["targets"]>[number];

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

function usd(value: number): string {
  return USD.format(Number.isFinite(value) ? value : 0);
}

function num(value: number): string {
  return new Intl.NumberFormat("en-US").format(Number.isFinite(value) ? value : 0);
}

function ts(value: string): string {
  if (!value) return "—";
  return value.replace("T", " ").slice(0, 19);
}

/** The single rule this page exists to obey: null means "not computed", and the
 *  reason is the value worth showing. */
function Measured({
  value,
  reason,
  render,
}: {
  value: number | string | null | undefined;
  reason?: string;
  render?: (v: number) => string;
}) {
  if (value === null || value === undefined) {
    return (
      <span
        className="font-data-mono text-body-sm text-tertiary"
        title="The API returned null for this metric — the reason is shown instead of a fabricated number"
      >
        not computed: {reason?.trim() || "no reason given by the API"}
      </span>
    );
  }
  if (typeof value === "number") {
    return <span className="font-data-mono tabular-nums text-on-surface">{render ? render(value) : num(value)}</span>;
  }
  return <span className="font-data-mono text-on-surface">{value}</span>;
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="border border-error/50 bg-error/10 text-error px-4 py-3 flex items-start gap-3"
    >
      <Icon name="error" className="text-base shrink-0" />
      <p className="text-body-sm flex-1 min-w-0 break-words">{message}</p>
      {onRetry ? (
        <Button variant="ghost" size="sm" onClick={onRetry} className="shrink-0 text-error">
          Retry
        </Button>
      ) : null}
    </div>
  );
}

function Panel({
  title,
  meta,
  children,
}: {
  title: string;
  meta?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border border-outline-variant bg-surface-container">
      <header className="px-4 py-2.5 border-b border-outline-variant bg-surface-container-high flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-data-label text-data-label uppercase text-on-surface-variant">{title}</h3>
        {meta ? <div className="text-body-sm text-on-surface-variant">{meta}</div> : null}
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function BudgetPage() {
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();

  const [jobId, setJobId] = useState(() => params.get("job_id") || getStoredJobId() || "");
  const [runId, setRunId] = useState(() => params.get("run_id") || "");
  const [runInput, setRunInput] = useState(() => params.get("run_id") || "");

  const [targets, setTargets] = useState<TargetRow[]>([]);
  const [targetsError, setTargetsError] = useState("");

  const [snapshot, setSnapshot] = useState<JobSpendSnapshot | null>(null);
  const [summary, setSummary] = useState<SpendStageSummary | null>(null);
  const [caps, setCaps] = useState<StageCapReport[]>([]);
  const [capScope, setCapScope] = useState<"run" | "job" | "config">("config");
  const [allowances, setAllowances] = useState<RunAllowance[]>([]);
  const [adjustments, setAdjustments] = useState<BudgetAdjustment[]>([]);

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [newBudget, setNewBudget] = useState("");
  const [reason, setReason] = useState("");
  const [actor, setActor] = useState("operator");
  const [adjustBusy, setAdjustBusy] = useState(false);
  const [adjustError, setAdjustError] = useState("");

  /* ------------------------------------------------------------- targets -- */

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const data = await apiGet<{ ok: boolean; targets: TargetRow[] }>("/api/phase2/targets");
        if (!alive) return;
        setTargets(data.targets || []);
        setTargetsError("");
      } catch (e) {
        if (!alive) return;
        setTargets([]);
        setTargetsError(formatApiError(e));
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  /* ---------------------------------------------------------------- load -- */

  const load = useCallback(async () => {
    setLoading(true);
    const next: Record<string, string> = {};

    // The five reads are independent: one 500 must not blank the whole screen,
    // so each failure is reported against its own panel.
    const [snap, sum, capsRes, allow, adj] = await Promise.allSettled([
      jobId ? currentSpend(jobId) : Promise.resolve(null),
      jobId ? spendSummary(jobId, runId) : Promise.resolve(null),
      stageCapReport(jobId, runId),
      jobId ? listRunAllowances({ job_id: jobId, run_id: runId || undefined, limit: 50 }) : Promise.resolve(null),
      jobId ? listBudgetAdjustments(jobId, 50) : Promise.resolve(null),
    ]);

    if (snap.status === "fulfilled") setSnapshot(snap.value ? snap.value.spend : null);
    else {
      setSnapshot(null);
      next.snapshot = formatApiError(snap.reason);
    }

    if (sum.status === "fulfilled") setSummary(sum.value ? sum.value.summary : null);
    else {
      setSummary(null);
      next.summary = formatApiError(sum.reason);
    }

    if (capsRes.status === "fulfilled") {
      setCaps(capsRes.value.caps || []);
      setCapScope(capsRes.value.usage_scope);
    } else {
      setCaps([]);
      next.caps = formatApiError(capsRes.reason);
    }

    if (allow.status === "fulfilled") setAllowances(allow.value ? allow.value.allowances || [] : []);
    else {
      setAllowances([]);
      next.allowances = formatApiError(allow.reason);
    }

    if (adj.status === "fulfilled") setAdjustments(adj.value ? adj.value.adjustments || [] : []);
    else {
      setAdjustments([]);
      next.adjustments = formatApiError(adj.reason);
    }

    setErrors(next);
    setLoading(false);
  }, [jobId, runId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const nextParams = new URLSearchParams(params);
    if (jobId) nextParams.set("job_id", jobId);
    else nextParams.delete("job_id");
    if (runId) nextParams.set("run_id", runId);
    else nextParams.delete("run_id");
    if (nextParams.toString() !== params.toString()) setParams(nextParams, { replace: true });
    // `params`/`setParams` identities change on every render of the router;
    // the ledger scope is what should drive the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, runId]);

  useEffect(() => {
    if (snapshot && !newBudget) setNewBudget(String(snapshot.budget_usd));
    // Only seeds the field once, so an operator's typing is never overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot?.job_id]);

  /* -------------------------------------------------------------- derived -- */

  const stageRows = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.stages || {}).sort((a, b) => b[1].cost_usd - a[1].cost_usd);
  }, [summary]);

  const pct =
    snapshot && snapshot.budget_usd > 0
      ? Math.min(100, (snapshot.spend_usd / snapshot.budget_usd) * 100)
      : 0;
  const locked = Boolean(snapshot?.budget_exceeded || snapshot?.status === "budget_exceeded");

  const submitAdjustment = useCallback(async () => {
    setAdjustError("");
    const parsed = Number.parseFloat(newBudget);
    if (!jobId) {
      setAdjustError("Select a target ledger first — an adjustment is always against one job_id.");
      return;
    }
    if (!Number.isFinite(parsed) || parsed < 0) {
      setAdjustError("Enter a budget in USD (a non-negative number).");
      return;
    }
    // The route audits `reason`; a blank one would store an unexplained ledger
    // change, so the UI refuses it before the request is made.
    if (!reason.trim()) {
      setAdjustError("A reason is required — every ledger adjustment is audited.");
      return;
    }
    setAdjustBusy(true);
    try {
      const data = await adjustBudget({
        job_id: jobId,
        budget_usd: parsed,
        reason: reason.trim(),
        actor: actor.trim() || "operator",
      });
      setSnapshot(data.spend);
      setReason("");
      toast(`Budget set to ${usd(parsed)}`);
      await load();
    } catch (e) {
      setAdjustError(formatApiError(e));
    } finally {
      setAdjustBusy(false);
    }
  }, [jobId, newBudget, reason, actor, toast, load]);

  return (
    <main className="flex flex-col flex-1 overflow-hidden bg-surface-dim min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col custom-scrollbar">
        <PageTitle
          title="Budget"
          meta="Target lifetime ledger, per-stage cost, stage caps with provenance, run allowances and the audited adjustment history."
          actions={
            <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
              <Icon name="refresh" className="text-base" />
              {loading ? "Loading…" : "Refresh"}
            </Button>
          }
        />

        {/* --------------------------------------------------------- scope -- */}
        <section className="px-6 py-3 border-b border-outline-variant bg-surface-container-low flex flex-wrap items-end gap-3">
          <Field label="Target ledger" className="w-72">
            <Select
              value={jobId}
              onChange={(e) => {
                setJobId(e.target.value);
                setRunId("");
                setRunInput("");
              }}
            >
              <option value="">No ledger selected (configuration only)</option>
              {targets
                .filter((t) => t.latest_job_id)
                .map((t) => (
                  <option key={t.latest_job_id} value={t.latest_job_id}>
                    {t.target_display_name}
                  </option>
                ))}
              {jobId && !targets.some((t) => t.latest_job_id === jobId) ? (
                <option value={jobId}>{jobId} (current)</option>
              ) : null}
            </Select>
          </Field>
          <Field label="job_id" className="w-80">
            <Input
              value={jobId}
              onChange={(e) => setJobId(e.target.value.trim())}
              placeholder="Canonical job UUID"
            />
          </Field>
          <Field label="run_id (optional — scopes usage to one run)" className="w-72">
            <Input
              value={runInput}
              onChange={(e) => setRunInput(e.target.value)}
              onBlur={() => setRunId(runInput.trim())}
              onKeyDown={(e) => {
                if (e.key === "Enter") setRunId(runInput.trim());
              }}
              placeholder="Leave empty for the whole job"
            />
          </Field>
          {targetsError ? (
            <p className="text-body-sm text-error w-full" role="alert">
              Target list unavailable: {targetsError} — the job_id field still works.
            </p>
          ) : null}
        </section>

        <div className="px-6 py-4 space-y-4">
          {!jobId ? (
            <div className="border border-tertiary/40 bg-tertiary/10 px-4 py-3 text-body-sm text-tertiary">
              No ledger selected. Stage caps below are a configuration read — their used and
              remaining columns report that nothing was counted, which is not the same as zero
              consumption. Ledger, per-stage cost, allowances and adjustments need a job_id.
            </div>
          ) : null}

          {/* ------------------------------------------------------ ledger -- */}
          <Panel
            title="Target ledger"
            meta={snapshot ? <span className="font-data-mono">{snapshot.job_id || "no job"}</span> : null}
          >
            {errors.snapshot ? <ErrorBanner message={errors.snapshot} onRetry={() => void load()} /> : null}
            {!errors.snapshot && !jobId ? (
              <p className="text-body-sm text-on-surface-variant">
                No ledger selected — spend, budget and remaining are not read. This page will not
                show a zero in place of a figure it has not measured.
              </p>
            ) : null}
            {snapshot && !errors.snapshot ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                  <div>
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Spend
                    </p>
                    <p className={`text-headline-lg font-black tabular-nums ${locked ? "text-error" : "text-primary"}`}>
                      {usd(snapshot.spend_usd)}
                    </p>
                  </div>
                  <div>
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Budget
                    </p>
                    <p className="text-headline-md font-data-mono tabular-nums text-on-surface">
                      {usd(snapshot.budget_usd)}
                    </p>
                  </div>
                  <div>
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Remaining
                    </p>
                    <p className="text-headline-md font-data-mono tabular-nums text-on-surface">
                      {usd(snapshot.remaining_usd)}
                    </p>
                  </div>
                  <div>
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Status
                    </p>
                    <p className={`font-data-mono ${locked ? "text-error" : "text-secondary"}`}>
                      {snapshot.status}
                    </p>
                  </div>
                </div>
                <div className="h-1.5 bg-surface-variant overflow-hidden">
                  <div
                    className={`h-full transition-all ${locked || pct >= 90 ? "bg-error" : "bg-primary"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {locked ? (
                  <p className="text-body-sm text-error font-semibold">
                    Budget exceeded — paid actions are disabled until the ledger is raised.
                  </p>
                ) : null}
                <p className="text-body-sm text-on-surface-variant font-data-mono">
                  evidence {num(snapshot.evidence_count)} / cap {num(snapshot.evidence_cap)}
                  {snapshot.evidence_cap_reached ? " · cap reached" : ""}
                </p>
              </div>
            ) : null}
          </Panel>

          {/* ------------------------------------------------ stage summary -- */}
          <Panel
            title="Per-stage cost, calls and tokens"
            meta={
              summary ? (
                <span className="font-data-mono">
                  {runId ? `run ${runId}` : "whole job"} · total {usd(summary.total_cost_usd)} ·{" "}
                  {num(summary.calls)} calls
                </span>
              ) : null
            }
          >
            {errors.summary ? <ErrorBanner message={errors.summary} onRetry={() => void load()} /> : null}
            {!errors.summary && !jobId ? (
              <p className="text-body-sm text-on-surface-variant">
                Select a target ledger to load per-stage spend.
              </p>
            ) : null}
            {!errors.summary && jobId && summary ? (
              <div className="space-y-4">
                {/* cache reuse + the cold/warm label, both with their reasons */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="border border-outline-variant bg-surface-container-lowest p-3">
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Cache reuse
                    </p>
                    <p className="mt-1">
                      <Measured
                        value={summary.cache_hit_share}
                        reason={summary.cold_vs_warm?.reason}
                        render={(v) => `${(v * 100).toFixed(1)}%`}
                      />
                    </p>
                    <p className="text-[11px] font-data-mono text-outline mt-1">
                      {num(summary.cache_hits)} of {num(summary.calls)} calls reused
                    </p>
                  </div>
                  <div className="border border-outline-variant bg-surface-container-lowest p-3">
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Cold / warm
                    </p>
                    <p className="mt-1">
                      <Measured
                        value={summary.cold_vs_warm?.label ?? null}
                        reason={summary.cold_vs_warm?.reason}
                      />
                    </p>
                    <p className="text-[11px] font-data-mono text-outline mt-1">
                      warm threshold {(Number(summary.cold_vs_warm?.threshold ?? 0) * 100).toFixed(0)}% reuse
                    </p>
                  </div>
                  <div className="border border-outline-variant bg-surface-container-lowest p-3">
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Cost / accepted project
                    </p>
                    <p className="mt-1">
                      <Measured
                        value={summary.cost_per_accepted_project}
                        reason={summary.cost_per_accepted_project_reason}
                        render={usd}
                      />
                    </p>
                    <p className="text-[11px] font-data-mono text-outline mt-1">
                      {num(summary.accepted_projects)} accepted
                    </p>
                  </div>
                  <div className="border border-outline-variant bg-surface-container-lowest p-3">
                    <p className="font-data-label text-data-label uppercase text-on-surface-variant">
                      Cost / useful update
                    </p>
                    <p className="mt-1">
                      <Measured
                        value={summary.cost_per_useful_update}
                        reason={summary.cost_per_useful_update_reason}
                        render={usd}
                      />
                    </p>
                    <p className="text-[11px] font-data-mono text-outline mt-1">
                      {num(summary.useful_updates)} useful updates
                      {summary.outcome_attribution ? ` · ${summary.outcome_attribution}` : ""}
                    </p>
                  </div>
                </div>

                {stageRows.length === 0 ? (
                  <p className="text-body-sm text-on-surface-variant">
                    No spend events recorded for this {runId ? "run" : "ledger"} yet — no stage has
                    billed anything.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-body-sm">
                      <thead>
                        <tr className="bg-surface-container-high text-left">
                          {["Stage", "Cost", "Calls", "Cache hits", "Prompt tok", "Completion tok", "Reserved units", "Operations"].map(
                            (h) => (
                              <th
                                key={h}
                                className="px-3 py-2 font-data-label text-data-label uppercase text-on-surface-variant whitespace-nowrap"
                              >
                                {h}
                              </th>
                            ),
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {stageRows.map(([stage, t]) => (
                          <tr key={stage} className="border-t border-outline-variant/60 align-top">
                            <td className="px-3 py-2 font-data-mono text-on-surface">{stage || "(unattributed)"}</td>
                            <td className="px-3 py-2 font-data-mono tabular-nums">{usd(t.cost_usd)}</td>
                            <td className="px-3 py-2 font-data-mono tabular-nums">{num(t.calls)}</td>
                            <td className="px-3 py-2 font-data-mono tabular-nums text-secondary">
                              {num(t.cache_hits)}
                            </td>
                            <td className="px-3 py-2 font-data-mono tabular-nums">{num(t.prompt_tokens)}</td>
                            <td className="px-3 py-2 font-data-mono tabular-nums">{num(t.completion_tokens)}</td>
                            <td className="px-3 py-2 font-data-mono tabular-nums">{num(t.reserved_units)}</td>
                            <td className="px-3 py-2">
                              <div className="flex flex-col gap-0.5">
                                {Object.entries(t.operations || {}).map(([op, o]) => (
                                  <span key={op} className="font-data-mono text-[11px] text-on-surface-variant">
                                    {op}: {usd(o.cost_usd)} · {num(o.calls)} calls
                                    {o.cache_hits ? ` · ${num(o.cache_hits)} cached` : ""}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </Panel>

          {/* --------------------------------------------------- stage caps -- */}
          <Panel
            title="Stage caps"
            meta={
              <span className="font-data-mono">
                usage scope: {capScope}
                {capScope === "config" ? " (nothing counted)" : ""}
              </span>
            }
          >
            {errors.caps ? <ErrorBanner message={errors.caps} onRetry={() => void load()} /> : null}
            {!errors.caps && caps.length === 0 ? (
              <p className="text-body-sm text-on-surface-variant">No stage caps configured.</p>
            ) : null}
            {!errors.caps && caps.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <thead>
                    <tr className="bg-surface-container-high text-left">
                      {["Cap", "Stage", "Ceiling", "Used", "Remaining", "Provenance"].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 font-data-label text-data-label uppercase text-on-surface-variant whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {caps.map((c) => (
                      <tr
                        key={c.key}
                        className={`border-t border-outline-variant/60 align-top ${c.exhausted ? "bg-error/10" : ""}`}
                      >
                        <td className="px-3 py-2 font-data-mono text-on-surface">{c.key}</td>
                        <td className="px-3 py-2 font-data-mono text-on-surface-variant">
                          {c.stage || "per document / mention / cluster"}
                        </td>
                        <td className="px-3 py-2">
                          <Measured
                            value={c.cap}
                            reason={c.stage ? "cap 0 means no ceiling for this stage" : "this cap is not counted per run"}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Measured
                            value={c.used}
                            reason={
                              c.stage
                                ? `nothing counted (usage source: ${c.usage_source || "none"})`
                                : "not counted per run"
                            }
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Measured
                            value={c.remaining}
                            reason={
                              c.stage
                                ? `nothing counted (usage source: ${c.usage_source || "none"})`
                                : "not counted per run"
                            }
                          />
                          {c.exhausted ? (
                            <span className="ml-2 text-error font-semibold">exhausted</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 font-data-mono text-[11px] text-on-surface-variant">
                          {c.env} = {String(c.value)}
                          <br />
                          <span className="text-outline">
                            default {String(c.default)} · source {c.source}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Panel>

          {/* ---------------------------------------------------- allowances -- */}
          <Panel
            title="Run allowances"
            meta={<span className="font-data-mono">{allowances.length} reservation(s)</span>}
          >
            {errors.allowances ? (
              <ErrorBanner message={errors.allowances} onRetry={() => void load()} />
            ) : null}
            {!errors.allowances && !jobId ? (
              <p className="text-body-sm text-on-surface-variant">
                Select a target ledger to load its run allowances.
              </p>
            ) : null}
            {!errors.allowances && jobId && allowances.length === 0 ? (
              <p className="text-body-sm text-on-surface-variant">
                No run allowance has been reserved against this ledger. Allowances appear when a v3
                run reserves a slice of the target budget before spending it.
              </p>
            ) : null}
            {!errors.allowances && allowances.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <thead>
                    <tr className="bg-surface-container-high text-left">
                      {["Run", "Reserved", "Consumed", "Released", "Overrun", "Created"].map((h) => (
                        <th
                          key={h}
                          className="px-3 py-2 font-data-label text-data-label uppercase text-on-surface-variant whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allowances.map((a) => (
                      <tr
                        key={`${a.run_id}:${a.created_at}`}
                        className={`border-t border-outline-variant/60 ${a.overran_reservation ? "bg-error/10" : ""}`}
                      >
                        <td className="px-3 py-2 font-data-mono text-on-surface">
                          <button
                            type="button"
                            className="text-primary hover:underline"
                            onClick={() => {
                              setRunId(a.run_id);
                              setRunInput(a.run_id);
                            }}
                            title="Scope this page to that run"
                          >
                            {a.run_id}
                          </button>
                        </td>
                        <td className="px-3 py-2 font-data-mono tabular-nums">{usd(a.reserved_usd)}</td>
                        <td className="px-3 py-2 font-data-mono tabular-nums">{usd(a.consumed_usd)}</td>
                        <td className="px-3 py-2 font-data-mono tabular-nums">{usd(a.released_usd)}</td>
                        <td
                          className={`px-3 py-2 font-data-mono tabular-nums ${
                            a.overran_reservation ? "text-error font-semibold" : "text-on-surface-variant"
                          }`}
                        >
                          {a.overran_reservation
                            ? `${usd(a.overrun_usd || a.consumed_usd - a.reserved_usd)} over`
                            : "within reservation"}
                        </td>
                        <td className="px-3 py-2 font-data-mono text-[11px] text-outline">
                          {ts(a.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </Panel>

          {/* --------------------------------------------------- adjustment -- */}
          <Panel title="Adjust the ledger">
            {!jobId ? (
              <p className="text-body-sm text-on-surface-variant">
                Select a target ledger before adjusting a budget.
              </p>
            ) : (
              <div className="space-y-3 max-w-2xl">
                <div className="grid sm:grid-cols-2 gap-3">
                  <Field label="New budget (USD)">
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={newBudget}
                      onChange={(e) => setNewBudget(e.target.value)}
                    />
                  </Field>
                  <Field label="Actor">
                    <Input value={actor} onChange={(e) => setActor(e.target.value)} />
                  </Field>
                </div>
                <Field label="Reason (required — this is audited)">
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why is this ledger being raised or cut?"
                  />
                </Field>
                {adjustError ? <ErrorBanner message={adjustError} /> : null}
                <Button
                  variant="primary"
                  disabled={adjustBusy || !reason.trim()}
                  title={!reason.trim() ? "A reason is required for every ledger adjustment" : undefined}
                  onClick={() => void submitAdjustment()}
                >
                  {adjustBusy ? "Saving…" : "Apply adjustment"}
                </Button>
                {!reason.trim() ? (
                  <p className="text-body-sm text-on-surface-variant">
                    Enter a reason to enable this button — the adjustment is written to
                    `budget_adjustments` with the actor and cannot be explained later.
                  </p>
                ) : null}
              </div>
            )}
          </Panel>

          {/* ------------------------------------------------------ history -- */}
          <Panel
            title="Adjustment history"
            meta={<span className="font-data-mono">{adjustments.length} record(s)</span>}
          >
            {errors.adjustments ? (
              <ErrorBanner message={errors.adjustments} onRetry={() => void load()} />
            ) : null}
            {!errors.adjustments && !jobId ? (
              <p className="text-body-sm text-on-surface-variant">
                Select a target ledger to load its adjustment history.
              </p>
            ) : null}
            {!errors.adjustments && jobId && adjustments.length === 0 ? (
              <EmptyState
                className="border border-outline-variant bg-surface-container-lowest"
                title="No adjustments recorded"
                description="This ledger still holds the budget it was created with. Every raise or cut made from this page is written here with its reason and actor."
              />
            ) : null}
            {!errors.adjustments && adjustments.length > 0 ? (
              <ul className="space-y-2">
                {adjustments.map((a) => (
                  <li
                    key={a.adjustment_id}
                    className="border border-outline-variant bg-surface-container-lowest px-3 py-2"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 font-data-mono text-body-sm">
                      <span className="text-on-surface-variant">{usd(a.previous_budget_usd)}</span>
                      <Icon name="arrow_forward" className="text-sm text-outline" />
                      <span className="text-on-surface">{usd(a.new_budget_usd)}</span>
                      <span className={a.delta_usd >= 0 ? "text-secondary" : "text-error"}>
                        {a.delta_usd >= 0 ? "+" : ""}
                        {usd(a.delta_usd)}
                      </span>
                      <span className="text-outline">· {a.actor || "unknown actor"}</span>
                      <span className="text-outline">· {ts(a.created_at)}</span>
                    </div>
                    <p className="text-body-sm text-on-surface-variant mt-1 break-words">
                      {a.reason || "(no reason stored)"}
                    </p>
                  </li>
                ))}
              </ul>
            ) : null}
          </Panel>
        </div>
      </div>
    </main>
  );
}
